import type { ExperienceItem } from "@/lib/types";
import { formatProviderDisplayName } from "./providerLinkState";
import { isExcursionItem } from "./marketplaceInventory";

// ─── Marketplace Comparison V1 ──────────────────────────────────────────────
// Pure, directly-testable decision logic for the comparison table. Every
// function here only ever reads fields the backend actually set — never
// infers, averages, or fabricates a value, and never declares a winner when
// data is missing or tied. No function here decides an overall "best
// option" — each one answers exactly one narrow, honest question about one
// attribute ("who's cheapest", not "who should I book").

/**
 * Comparador UX Redesign V1 — raised from 3 to 6 (brief task 1: "soportar
 * entre 2 y 6 actividades seleccionadas"). The desktop table's horizontal
 * scroll + sticky label column and the mobile card-per-item layout were
 * both already built to handle an arbitrary item count (see
 * ComparisonDrawer.tsx), so this is a pure limit change, no layout rework
 * required. Re-exported from SelectionTray.tsx for callers that already
 * import it from there. */
export const MAX_COMPARISON_ITEMS = 6;

export type AddToComparisonDecision = "add" | "noop_already_selected" | "blocked";

/**
 * Comparador UX Redesign V1, task 1 — "si ya hay 6, bloquear nuevas
 * selecciones con feedback claro": at capacity, adding a NEW item is
 * simply refused (never a silent replace-the-oldest, and never a second
 * "which one do I remove" dialog forced on the caller) — the caller is
 * expected to surface an honest, dismissable message (e.g. a toast) and
 * let the user remove something themselves first. A duplicate add of an
 * already-selected id is always a no-op, even at capacity — trying to
 * add the 6th item again must never read as "blocked".
 */
export function getAddToComparisonDecision(
  compositeId: string,
  selectedIds: ReadonlySet<string>,
  maxItems: number = MAX_COMPARISON_ITEMS,
): AddToComparisonDecision {
  if (selectedIds.has(compositeId)) return "noop_already_selected";
  if (selectedIds.size >= maxItems) return "blocked";
  return "add";
}

export interface ComparisonWinner {
  compositeId: string;
  label:       string;
}

/**
 * "Menor precio" — requires every compared item to have a real price AND
 * the same currency (comparing raw numbers across currencies would be a
 * fabricated comparison, not a real one). Returns null on any missing
 * price, any currency mismatch, or a tie for the minimum.
 */
export function getLowestPriceWinner(items: readonly ExperienceItem[]): ComparisonWinner | null {
  if (items.length < 2) return null;
  if (items.some(exp => exp.price_from == null)) return null;
  const currencies = new Set(items.map(exp => exp.currency || "USD"));
  if (currencies.size > 1) return null;

  const prices = items.map(exp => exp.price_from!);
  const min = Math.min(...prices);
  const winners = items.filter(exp => exp.price_from === min);
  if (winners.length !== 1) return null;
  return { compositeId: winners[0].id, label: "Menor precio" };
}

/**
 * Marketplace Comparison V3 — a rating only carries real signal once enough
 * people have actually rated it. Without a floor, a single 5.0 review would
 * beat a well-established 4.6 with 900 reviews, which is a worse decision
 * signal, not a better one. This mirrors this codebase's Bayesian principle
 * (see CLAUDE.md Regla 23) in spirit — low evidence should never win — kept
 * as a simple floor here rather than a full Bayesian recompute, since this
 * is a display-only "is this winner honest enough to show" gate, not a
 * ranking score.
 */
const MIN_REVIEWS_FOR_RATING_WINNER = 10;

/**
 * "Mejor valoración" — requires every compared item to have a real,
 * positive rating AND a real review_count of at least
 * MIN_REVIEWS_FOR_RATING_WINNER (task 4: never declare a winner from a
 * high rating backed by too little evidence). Returns null on any missing/
 * under-evidenced rating or a tie for the max.
 */
export function getBestRatingWinner(items: readonly ExperienceItem[]): ComparisonWinner | null {
  if (items.length < 2) return null;
  if (items.some(exp => typeof exp.rating !== "number" || exp.rating <= 0)) return null;
  if (items.some(exp => typeof exp.review_count !== "number" || exp.review_count < MIN_REVIEWS_FOR_RATING_WINNER)) return null;

  const max = Math.max(...items.map(exp => exp.rating!));
  const winners = items.filter(exp => exp.rating === max);
  if (winners.length !== 1) return null;
  return { compositeId: winners[0].id, label: "Mejor valoración" };
}

/**
 * "Más reseñas" — requires every compared item to have a real, positive
 * review count. Independent of the rating check above: an item can have a
 * real rating with no review_count (or vice versa), so this never assumes
 * one implies the other.
 */
export function getMostReviewsWinner(items: readonly ExperienceItem[]): ComparisonWinner | null {
  if (items.length < 2) return null;
  if (items.some(exp => typeof exp.review_count !== "number" || exp.review_count <= 0)) return null;

  const max = Math.max(...items.map(exp => exp.review_count!));
  const winners = items.filter(exp => exp.review_count === max);
  if (winners.length !== 1) return null;
  return { compositeId: winners[0].id, label: "Más reseñas" };
}

/**
 * "Menor duración" — requires every compared item to have a real,
 * positive duration_hours. Returns null on any missing duration or a tie
 * for the minimum.
 */
export function getShortestDurationWinner(items: readonly ExperienceItem[]): ComparisonWinner | null {
  if (items.length < 2) return null;
  if (items.some(exp => typeof exp.duration_hours !== "number" || exp.duration_hours <= 0)) return null;

  const min = Math.min(...items.map(exp => exp.duration_hours));
  const winners = items.filter(exp => exp.duration_hours === min);
  if (winners.length !== 1) return null;
  return { compositeId: winners[0].id, label: "Menor duración" };
}

/**
 * All four decision helpers at once, keyed by their own label — convenience
 * for the comparison table, which needs to look up "is this cell the
 * winner for its row" without recomputing on every render.
 */
export function getComparisonWinners(items: readonly ExperienceItem[]): ComparisonWinner[] {
  return [
    getLowestPriceWinner(items),
    getBestRatingWinner(items),
    getMostReviewsWinner(items),
    getShortestDurationWinner(items),
  ].filter((w): w is ComparisonWinner => w !== null);
}

/**
 * Non-blocking compatibility note — task 2/task 5 (Comparison V3): "advertir
 * cuando las categorías o focos sean poco comparables, pero no bloquear
 * innecesariamente." Two independent triggers, checked in order (only one
 * note is ever shown at a time):
 *
 * 1. Every compared item has a different category (zero overlap) — the
 *    case where a side-by-side comparison is least apples-to-apples.
 *    Sharing at least one category anywhere in the set is enough to stay
 *    quiet.
 * 2. The set mixes an excursion/day-trip/transit-layover item with a core
 *    city experience (isExcursionItem() — the same single source of truth
 *    the main inventory list uses to split "Experiencias en la ciudad" from
 *    "Excursiones y viajes de varios días"). A 2-hour city walking tour and
 *    a 10-hour day trip to another town are structurally different
 *    products even when they happen to share a category tag.
 *
 * cityNames is optional and only sharpens trigger 2's "desde {city}"
 * signal — safe to omit, the rest of isExcursionItem's heuristics still work.
 *
 * Never blocks — this is advisory copy only, read by the caller, never used
 * to disable adding/comparing.
 */
export function getCategoryCompatibilityNote(
  items: readonly ExperienceItem[],
  cityNames: readonly string[] = [],
): string | null {
  if (items.length < 2) return null;

  const categories = new Set(items.map(exp => exp.category));
  if (categories.size === items.length) {
    return "Estás comparando experiencias de categorías distintas — la comparación puede ser menos directa.";
  }

  const excursionFlags = new Set(items.map(exp => isExcursionItem(exp, cityNames)));
  if (excursionFlags.size > 1) {
    return "Son experiencias de distinto tipo; compara especialmente duración, precio y proveedor.";
  }

  return null;
}

export interface KeyDifference {
  /** The item this difference points at, or null for a set-wide note (e.g.
   *  provider commonality) that isn't about one specific "winning" item. */
  compositeId: string | null;
  text:        string;
}

/**
 * Provider-commonality note for the decision summary (task 2): honest only
 * — never invents a provider, only reports what's actually there. Needs at
 * least 2 provider items to say anything (a single provider item, or a set
 * that's entirely local/reference, has nothing to compare).
 */
function getProviderCommonalityDifference(items: readonly ExperienceItem[]): KeyDifference | null {
  const providerItems = items.filter(exp => exp.source_type === "provider");
  if (providerItems.length < 2) return null;

  // A commonality claim ("todas abren con X" / "proveedores: X / Y") is
  // only honest when every provider item has a REAL provider_name — never
  // fall back to a generic "el proveedor" label just to force a bullet.
  const displayNames = providerItems.map(exp => formatProviderDisplayName(exp.provider_name));
  if (displayNames.some(name => name === null)) return null;

  const names = new Set(displayNames as string[]);
  if (names.size > 1) {
    return { compositeId: null, text: `Proveedores: ${Array.from(names).join(" / ")}` };
  }
  if (providerItems.length === items.length) {
    const [name] = names;
    return { compositeId: null, text: `Todas abren con ${name}` };
  }
  return null;
}

/**
 * Marketplace Comparison V3, task 2 — "Diferencias clave": at most 4 short,
 * honest bullets for the decision summary, built ONLY from the same winner
 * functions already used by the attribute rows (never a separate/looser
 * check — a difference shown here must also be the badge shown in the
 * table). Priority order when more than 4 would qualify: price, rating,
 * duration, reviews, then provider commonality — the four per-attribute
 * winners come first since each names a specific item to look at; the
 * provider note is set-wide context, so it's the first one dropped when
 * everything else already has something to say.
 */
export function getKeyDifferences(items: readonly ExperienceItem[]): KeyDifference[] {
  if (items.length < 2) return [];

  const differences: KeyDifference[] = [];
  const priceWinner = getLowestPriceWinner(items);
  if (priceWinner) differences.push({ compositeId: priceWinner.compositeId, text: "Menor precio" });
  const ratingWinner = getBestRatingWinner(items);
  if (ratingWinner) differences.push({ compositeId: ratingWinner.compositeId, text: "Mejor valoración con reseñas suficientes" });
  const durationWinner = getShortestDurationWinner(items);
  if (durationWinner) differences.push({ compositeId: durationWinner.compositeId, text: "Más corta" });
  const reviewsWinner = getMostReviewsWinner(items);
  if (reviewsWinner) differences.push({ compositeId: reviewsWinner.compositeId, text: "Más reseñas" });

  if (differences.length < 4) {
    const providerNote = getProviderCommonalityDifference(items);
    if (providerNote) differences.push(providerNote);
  }

  return differences.slice(0, 4);
}